// Part of the landing page (split out of pages/Landing.js, 2026-09-24).
import React, { useState, useEffect } from 'react';

// ── Retailer comparison strip ───────────────────────────────────────────────
// Brand-colored wordmark chips, not logo images — nominative use, no assets.
// Colors are recognisable approximations of each retailer's brand palette.
// Per-market, mirroring retailerCompareService.js: names must exist in that
// market's whitelist (AU_RETAILERS: 67, US_RETAILERS: 35) so the banner never
// promises a retailer the comparison surface can't deliver.
const RETAILER_STRIPS = {
  AU: {
    label: '60+ AUSSIE RETAILERS',
    chips: [
      { name: 'JB Hi-Fi',          bg: '#FFE500', fg: '#131A22' },
      { name: 'Officeworks',       bg: '#005DAA', fg: '#FFFFFF' },
      { name: 'Target',            bg: '#CC0000', fg: '#FFFFFF' },
      { name: 'Kmart',             bg: '#0054A4', fg: '#FFFFFF' },
      { name: 'Big W',             bg: '#0050A5', fg: '#FFFFFF' },
      { name: 'Bing Lee',          bg: '#002E6D', fg: '#FFFFFF' },
      { name: 'The Good Guys',     bg: '#E4002B', fg: '#FFFFFF' },
      { name: 'Harvey Norman',     bg: '#003DA5', fg: '#FFFFFF' },
      { name: 'Bunnings',          bg: '#0D5257', fg: '#FFFFFF' },
      { name: 'Chemist Warehouse', bg: '#FFDE00', fg: '#003C71' },
      { name: '+57 more', bg: '#FBFAF7', fg: '#131A22', more: true },
    ],
  },
  US: {
    label: '30+ US RETAILERS',
    chips: [
      { name: 'Walmart',        bg: '#0071CE', fg: '#FFFFFF' },
      { name: 'Best Buy',       bg: '#FFE000', fg: '#131A22' },
      { name: 'Target',         bg: '#CC0000', fg: '#FFFFFF' },
      { name: 'The Home Depot', bg: '#F96302', fg: '#FFFFFF' },
      { name: "Lowe's",         bg: '#004990', fg: '#FFFFFF' },
      { name: 'Costco',         bg: '#E31837', fg: '#FFFFFF' },
      { name: 'GameStop',       bg: '#1A1A1A', fg: '#FFFFFF' },
      { name: 'Chewy',          bg: '#1C49C2', fg: '#FFFFFF' },
      { name: 'REI',            bg: '#3E5C43', fg: '#FFFFFF' },
      { name: 'Walgreens',      bg: '#C8102E', fg: '#FFFFFF' },
      { name: '+25 more', bg: '#FBFAF7', fg: '#131A22', more: true },
    ],
  },
};

// Visitor market for the strip: saved Default Region first (same key the
// AddProductForm search uses), then IP geolocation, defaulting to AU (the
// site's home market — also shown to regions with no comparison support,
// as a showcase of the feature rather than a claim about their region).
function useVisitorMarket() {
  const [market, setMarket] = useState(() => {
    const saved = localStorage.getItem('ph_default_region');
    if (!saved) return null;
    return saved === 'com' ? 'US' : 'AU';
  });
  useEffect(() => {
    if (market) return;
    let alive = true;
    fetch('https://ipapi.co/country/')
      .then(r => r.text())
      .then(code => { if (alive) setMarket(code.trim() === 'US' ? 'US' : 'AU'); })
      .catch(() => { if (alive) setMarket('AU'); });
    return () => { alive = false; };
  }, [market]);
  return market || 'AU';
}

function RetailerChipSet({ chips, hidden }) {
  return (
    <div className="l-retailers-set" aria-hidden={hidden || undefined}>
      {chips.map((r) => (
        <span
          key={r.name}
          className={`l-retailer-chip${r.more ? ' l-retailer-chip--more' : ''}`}
          style={{ background: r.bg, color: r.fg }}
        >
          {r.name}
        </span>
      ))}
    </div>
  );
}

function RetailerStrip() {
  const market = useVisitorMarket();
  const strip = RETAILER_STRIPS[market];
  return (
    <section className="l-retailers" aria-label="Retailers we compare prices across">
      <div className="l-retailers-label">
        &#x25C6; COMPARES PRICES ACROSS {strip.label}
        <span className="l-reg-star" title="Available with a free registered account">&nbsp;*</span>
      </div>
      <div className="l-retailers-track">
        <RetailerChipSet chips={strip.chips} />
        <RetailerChipSet chips={strip.chips} hidden />
      </div>
    </section>
  );
}

export { RETAILER_STRIPS, useVisitorMarket, RetailerChipSet, RetailerStrip };
